import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'isBulsun.com';
const SITE_URL = 'https://isbulsun.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const COMPANY = 'HireThink Yapay Zeka Teknolojileri A.Ş.';

const SEO = ({
  title,
  description,
  keywords,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  structuredData,
  children,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Yapay Zeka Destekli Kariyer ve İlan Otomasyon Asistanı`;
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      {structuredData &&
        (Array.isArray(structuredData) ? structuredData : [structuredData]).map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}

      {children}
    </Helmet>
  );
};

// Pre-built structured data generators
export const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  alternateName: 'isBulsun',
  url: SITE_URL,
  description: 'Yapay zeka destekli kariyer ve ilan otomasyon asistanı. CV analizi, akıllı ilan eşleştirme, form taslağı hazırlama ve kariyer gelişimi.',
  publisher: {
    '@type': 'Organization',
    name: COMPANY,
    url: 'https://hirethink.ai',
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: COMPANY,
  url: 'https://hirethink.ai',
  logo: `${SITE_URL}/logo512.png`,
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: 'Turkish',
  },
};

export function jobPostingSchema(job) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title || '',
    description: job.description || '',
    datePosted: job.publishDate || job.createdAt || new Date().toISOString(),
    hiringOrganization: {
      '@type': 'Organization',
      name: job.companyName || '',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.city || job.location || '',
        addressCountry: 'TR',
      },
    },
  };

  if (job.validThrough || job.endDate) {
    schema.validThrough = job.validThrough || job.endDate;
  }
  if (job.employmentType || job.workingType) {
    schema.employmentType = job.employmentType || job.workingType;
  }
  if (job.salary || job.minSalary) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: 'TRY',
      value: {
        '@type': 'QuantitativeValue',
        value: job.salary || job.minSalary,
        unitText: 'MONTH',
      },
    };
  }

  return schema;
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  };
}

export default SEO;

// SSS şeması — hem klasik zengin sonuçlar hem de yapay zeka aramaları için
export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
}

// Sunulan hizmetler — arama motorlarının ve LLM'lerin listeleyebilmesi için
export function serviceListSchema(services) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((srv, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: srv.name,
        description: srv.description,
        provider: { '@type': 'Organization', name: SITE_NAME },
        areaServed: srv.areaServed || 'TR',
      },
    })),
  };
}
